import { Box, Grid, Stack, Typography, Collapse } from "@mui/material";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import ClearIcon from "@mui/icons-material/Clear";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Select } from "@/components/elements/Select";
import { useGetNationalityList } from "@/api/services/global/master";
import { blue, divider, black } from "@/theme/colors";
import { InnerBox } from "@/components/elements/InnerBox";
import { useFormContext, useFieldArray, useWatch } from "react-hook-form";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { CollapsibleSectionHeader, useCollapsible } from "@/features/talent/components/talent-common/CollapsibleSectionHeader";
import { YEAR_MONTH_STR_FORMAT } from "@/constants/datetime";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { sortNationalityOptions } from "@/features/talent/utils/sortNationalityOptions";

type SortableWorkExperienceItemProps = {
  fieldId: string;
  index: number;
  totalItems: number;
  control: ReturnType<typeof useFormContext<TTalentForm>>["control"];
  nationalityOptions: { label: string; value: string | number }[];
  onRemove: (index: number) => void;
  onAdd: (index: number) => void;
  canAdd: boolean;
};

const SortableWorkExperienceItem = ({
  fieldId,
  index,
  totalItems,
  control,
  nationalityOptions,
  onRemove,
  onAdd,
  canAdd,
}: SortableWorkExperienceItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: fieldId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <InnerBox mt={3} p={2} pt={0} showDivider={false} borderColor={divider.high} bordered={true}>
        {totalItems > 1 && (
          <Stack direction="row" alignItems="center" mt={2} mb={1} spacing={0.5}>
            <Box {...attributes} {...listeners} sx={{ cursor: "grab", display: "flex", alignItems: "center" }}>
              <DragIndicatorIcon sx={{ color: black[80], fontSize: 24 }} />
            </Box>
            <Typography variant="body14Bold" color={black[30]}>
              職歴{index + 1}
            </Typography>
          </Stack>
        )}

        <Stack mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormField control={control} name={`workExperiences.${index}.jobType`} label="職種">
                <Input placeholder="入力してください" />
              </FormField>
            </Grid>
          </Grid>
        </Stack>

        <Stack mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormField control={control} name={`workExperiences.${index}.companyName`} label="企業名">
                <Input placeholder="入力してください" />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name={`workExperiences.${index}.countryId`} label="国名">
                <Select options={nationalityOptions} />
              </FormField>
            </Grid>
          </Grid>
        </Stack>

        <Stack mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormField control={control} name={`workExperiences.${index}.startDate`} label="入社年月">
                <InputDatePicker
                  minDate={dayjs("1900-01-01")}
                  maxDate={dayjs()}
                  views={["year", "month"]}
                  openTo="month"
                  format={YEAR_MONTH_STR_FORMAT}
                />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name={`workExperiences.${index}.endDate`} label="退職年月">
                <InputDatePicker minDate={dayjs("1900-01-01")} views={["year", "month"]} openTo="month" format={YEAR_MONTH_STR_FORMAT} />
              </FormField>
            </Grid>
          </Grid>
        </Stack>
      </InnerBox>

      <Stack direction="row" alignItems="center" spacing={3} mt={1}>
        {canAdd && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer" }} onClick={() => onAdd(index)}>
            <AddCircleOutlineIcon sx={{ color: blue[80], fontSize: 18 }} />
            <Typography variant="cap12">職歴を追加</Typography>
          </Stack>
        )}

        {totalItems > 1 && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer", width: "fit-content" }} onClick={() => onRemove(index)}>
            <ClearIcon sx={{ color: black[80], fontSize: 18, display: "flex" }} />
            <Typography variant="cap12">削除</Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export const TalentFormWorkExperience = () => {
  const { control, trigger, formState, getValues } = useFormContext<TTalentForm>();
  const { nationalityOptions } = useGetNationalityList();
  const talentNationalityId = useWatch({ control, name: "nationalityId" });
  const sortedNationalityOptions = useMemo(
    () => sortNationalityOptions(nationalityOptions, talentNationalityId),
    [nationalityOptions, talentNationalityId],
  );
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  const { expanded, toggle } = useCollapsible();

  const { fields, append, replace } = useFieldArray({
    control,
    name: "workExperiences",
  });

  const workExperiences = useWatch({ control, name: "workExperiences" }) || [];

  // Flag to skip re-validation when adding a new item
  const isAddingRef = useRef(false);
  useEffect(() => {
    if (isAddingRef.current) {
      isAddingRef.current = false;
      return;
    }
    if (formState.isSubmitted) {
      trigger("workExperiences");
    }
  }, [workExperiences, trigger, formState.isSubmitted]);

  useEffect(() => {
    if (fields.length === 0) {
      append({
        jobType: "",
        companyName: "",
        countryId: null,
        startDate: null,
        endDate: null,
      });
    }
  }, [fields.length, append]);

  const newWorkExperience = { jobType: "", companyName: "", countryId: null, startDate: null, endDate: null };

  const handleAddField = () => {
    if (fields.length < 10) {
      isAddingRef.current = true;
      const currentValues = getValues("workExperiences") || [];
      replace([...currentValues, newWorkExperience]);
      setRenderKey((k) => k + 1);
    }
  };

  const handleInsertField = (afterIndex: number) => {
    if (fields.length < 10) {
      isAddingRef.current = true;
      const currentValues = getValues("workExperiences") || [];
      const updated = [...currentValues];
      updated.splice(afterIndex + 1, 0, newWorkExperience);
      replace(updated);
      setRenderKey((k) => k + 1);
    }
  };

  const handleRemoveField = (index: number) => {
    if (fields.length > 1) {
      const experience = workExperiences[index];
      if (experience && !experience.jobType?.trim() && !experience.companyName?.trim() && !experience.startDate && !experience.endDate) {
        const currentValues = getValues("workExperiences") || [];
        const updated = currentValues.filter((_: unknown, i: number) => i !== index);
        replace(updated);
        setRenderKey((k) => k + 1);
      } else {
        setDeleteIndex(index);
        setIsDeleteModalOpen(true);
      }
    }
  };

  const confirmDelete = () => {
    if (deleteIndex !== null && fields.length > 1) {
      const currentValues = getValues("workExperiences") || [];
      const updated = currentValues.filter((_: unknown, i: number) => i !== deleteIndex);
      replace(updated);
      setRenderKey((k) => k + 1);
    }
    setIsDeleteModalOpen(false);
    setDeleteIndex(null);
  };

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      const currentValues = getValues("workExperiences") || [];
      const reordered = [...currentValues];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);
      replace(reordered);
      setRenderKey((k) => k + 1);
    }
  };

  const fieldIds = fields.map((f) => f.id);

  return (
    <>
      <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
        <CollapsibleSectionHeader title="職歴" expanded={expanded} onToggle={toggle} />

        <Collapse in={expanded}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
              {fields.map((field, index) => (
                <SortableWorkExperienceItem
                  key={`${field.id}-${renderKey}`}
                  fieldId={field.id}
                  index={index}
                  totalItems={fields.length}
                  control={control}
                  nationalityOptions={sortedNationalityOptions}
                  onRemove={handleRemoveField}
                  onAdd={handleInsertField}
                  canAdd={fields.length < 10}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Collapse>
      </InnerBox>

      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteIndex(null);
        }}
        onOk={confirmDelete}
        message="「職歴」を削除してもよろしいですか？"
      />
    </>
  );
};
